export type AppThemeName = 'blackGold' | 'light'

export interface AppThemeConfig {
  currentTheme: AppThemeName
  fallbackTheme: AppThemeName
  cdnBaseUrl: string
  cdnFolders: Record<AppThemeName, string>
  themes: Record<AppThemeName, {
    label: string
    colors: Record<string, string>
    assets: Record<string, string>
  }>
}

const STORAGE_KEY = 'youyu_theme_override'
const DEBUG_VISIBLE_KEY = 'youyu_theme_debug_visible'

import themeConfig from './theme_config.json'

export const appThemeConfig = themeConfig as AppThemeConfig

export const appThemeNames = Object.keys(appThemeConfig.themes) as AppThemeName[]

const isThemeName = (value: string | null | undefined): value is AppThemeName =>
  !!value && Object.prototype.hasOwnProperty.call(appThemeConfig.themes, value)

const getQueryTheme = () => {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get('theme') || params.get('skin')
}

export const getCurrentThemeName = (): AppThemeName => {
  if (typeof window === 'undefined') return appThemeConfig.currentTheme
  const queryTheme = getQueryTheme()
  if (isThemeName(queryTheme)) {
    localStorage.setItem(STORAGE_KEY, queryTheme)
    return queryTheme
  }
  const overrideTheme = localStorage.getItem(STORAGE_KEY)
  if (isThemeName(overrideTheme)) return overrideTheme
  return appThemeConfig.currentTheme
}

export const applyAppTheme = (themeName = getCurrentThemeName()) => {
  const theme = appThemeConfig.themes[themeName] || appThemeConfig.themes[appThemeConfig.fallbackTheme]
  const root = document.documentElement
  root.dataset.theme = themeName
  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value)
  })
  window.dispatchEvent(new CustomEvent('app-theme-change', { detail: { theme: themeName } }))
}

export const initAppTheme = () => {
  if (typeof window === 'undefined') return
  applyAppTheme()
}

export const setThemeOverride = (themeName?: AppThemeName | null) => {
  if (typeof window === 'undefined') return
  if (themeName) localStorage.setItem(STORAGE_KEY, themeName)
  else localStorage.removeItem(STORAGE_KEY)
  applyAppTheme(themeName || appThemeConfig.currentTheme)
}

export const isThemeDebugVisible = () => {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.get('debugTheme') === '1' || localStorage.getItem(DEBUG_VISIBLE_KEY) === '1'
}

export const setThemeDebugVisible = (visible: boolean) => {
  if (typeof window === 'undefined') return
  if (visible) localStorage.setItem(DEBUG_VISIBLE_KEY, '1')
  else localStorage.removeItem(DEBUG_VISIBLE_KEY)
}

export const themeAsset = (key: string) => {
  const themeName = getCurrentThemeName()
  const theme = appThemeConfig.themes[themeName] || appThemeConfig.themes[appThemeConfig.fallbackTheme]
  const path = theme.assets[key] || appThemeConfig.themes[appThemeConfig.fallbackTheme].assets[key] || key
  if (/^(https?:)?\/\//.test(path) || path.startsWith('/') || path.startsWith('data:')) return path
  return `${appThemeConfig.cdnBaseUrl}/${path.replace(/^\/+/, '')}`
}

export const themeCdnAsset = (fileName: string, themeName = getCurrentThemeName()) => {
  const folder = appThemeConfig.cdnFolders[themeName] || appThemeConfig.cdnFolders[appThemeConfig.fallbackTheme]
  return `${appThemeConfig.cdnBaseUrl}/${folder}/${fileName.replace(/^\/+/, '')}`
}
